import React, { memo, useState, useCallback, forwardRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { Box, xcss } from '@atlaskit/primitives';
import { SingleLineTextInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-single-line-text-inline-edit-full/src/ui/single-line-text/index.tsx';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import {
	type HoverPopoverContentRef,
	useHoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { TruncatedTextWithPopup } from '../../truncated-text-cell-with-popup/index.tsx';
import {
	useIsDensityFull,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import { DUPLICATE_FIELD_EDIT_WIDTH, MAX_LINE_WRAP_NEW } from '../../../constants.tsx';
import type { PlainTextCellOld_PlainTextCellOld$key } from './__generated__/PlainTextCellOld_PlainTextCellOld.graphql';
import type { PlainTextCellOld$key } from './__generated__/PlainTextCellOld.graphql';

// Attributes for inline editing track events
const attributes = {
	// If the update was made inline (and not via the issue view)
	isInlineEditing: true,
};

export type Props = {
	fieldRef: PlainTextCellOld$key;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const PlainTextCell = memo<Props>(({ fieldRef, isFirstField, hasDuplicates }: Props) => {
	const data = useFragment<PlainTextCellOld$key>(
		graphql`
			fragment PlainTextCellOld on JiraSingleLineTextField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...PlainTextCellOld_PlainTextCellOld
				...singleLineText_issueFieldSingleLineTextInlineEditFull_SingleLineTextInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;

	useLazyLoadEditability(data.id, isEditable);

	const isDensityCompact = !useIsDensityFull();
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	const renderCustomReadView = useCallback(
		(text: string | null) => {
			if (!text) {
				return <EmptyFieldText />;
			}

			if (isSingleLineRowHeightEnabled) {
				if (hoverPopoverRefFactory) {
					let hoverPopoverRef: HoverPopoverContentRef = null;
					hoverPopoverRef = hoverPopoverRefFactory({
						content: text,
					});

					return (
						<Text
							ref={hoverPopoverRef}
							maxLines={MAX_LINE_WRAP_NEW}
							testId="native-issue-table.common.ui.issue-cells.plain-text.text"
						>
							{text}
						</Text>
					);
				}
			}

			return <Text maxLines={isDensityCompact ? 2 : undefined}>{text}</Text>;
		},
		[isDensityCompact, isSingleLineRowHeightEnabled, hoverPopoverRefFactory],
	);
	if (!isInlineEditingExtendedFieldSupportEnabled) {
		return <PlainTextCellOld fieldRef={data} />;
	}

	if (hasDuplicates && isSingleLineRowHeightEnabled) {
		return (
			<SingleLineTextInlineEditViewWithIsEditable
				attributes={attributes}
				spacing="default"
				editViewPopup
				editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
				editViewPopupAlignBlock="start"
				// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
				editViewPopupTarget={isFirstField ? CustomTarget : undefined}
				fragmentRef={data}
				isEditable={isEditable ?? false}
				maxLength={255}
				readViewFitContainerHeight
				renderCustomReadView={isFirstField ? renderCustomReadView : undefined}
				hideEditButton
				hasReadViewTruncateDisabled={isSingleLineRowHeightEnabled && isFirstField}
			/>
		);
	}

	return (
		<SingleLineTextInlineEditViewWithIsEditable
			attributes={attributes}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={isEditable ?? false}
			maxLength={255}
			readViewFitContainerHeight
			renderCustomReadView={renderCustomReadView}
			hideEditButton
			hasReadViewTruncateDisabled={isSingleLineRowHeightEnabled}
		/>
	);
});

export type PlainTextCellOldProps = {
	fieldRef: PlainTextCellOld_PlainTextCellOld$key;
	/**
	 * Should only be needed For VR testing, to render a storybook example with
	 * the popup content immediately visible
	 */
	shouldOpenOnFirstRender?: boolean;
};

export const PlainTextCellOld = memo<PlainTextCellOldProps>(
	({ fieldRef, shouldOpenOnFirstRender = false }: PlainTextCellOldProps) => {
		const data = useFragment<PlainTextCellOld_PlainTextCellOld$key>(
			graphql`
				fragment PlainTextCellOld_PlainTextCellOld on JiraSingleLineTextField {
					id
					text
				}
			`,
			fieldRef,
		);
		const { text } = data;
		const [isOpen, setIsOpen] = useState(shouldOpenOnFirstRender);

		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

		const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
			? // eslint-disable-next-line  react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

		let hoverPopoverRef: HoverPopoverContentRef = null;
		if (hoverPopoverRefFactory) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content: text,
			});
		}

		if (isSingleLineRowHeightEnabled) {
			return (
				<Box paddingBlock="space.050" paddingInline="space.100">
					<Text
						ref={hoverPopoverRef}
						maxLines={MAX_LINE_WRAP_NEW}
						testId="native-issue-table.common.ui.issue-cells.plain-text.text"
					>
						{text}
					</Text>
				</Box>
			);
		}

		return (
			<TruncatedTextWithPopup
				isOpen={isOpen}
				onSetIsOpen={setIsOpen}
				// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
				content={<p>{text}</p>}
				triggerText={text ?? null}
			/>
		);
	},
);

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return (
		<Box
			xcss={fg('nin_issue_table_rtl_fix') ? customTargetStyles : customTargetStylesOld}
			ref={ref}
		/>
	);
});

const customTargetStylesOld = xcss({
	width: 'space.0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	insetBlockStart: 'space.0',
});
